import React, { useState, useEffect } from 'react';
import {
  Box, Container, Paper, Typography, Grid, Card, CardActionArea, CardContent,
  Avatar, Chip, Divider, Snackbar, Alert, CircularProgress, Button,
} from '@mui/material';
import StorefrontOutlinedIcon          from '@mui/icons-material/StorefrontOutlined';
import PointOfSaleOutlinedIcon          from '@mui/icons-material/PointOfSaleOutlined';
import SchoolOutlinedIcon               from '@mui/icons-material/SchoolOutlined';
import NewspaperOutlinedIcon            from '@mui/icons-material/NewspaperOutlined';
import AccountTreeOutlinedIcon          from '@mui/icons-material/AccountTreeOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import BadgeOutlinedIcon                from '@mui/icons-material/BadgeOutlined';
import VerifiedIcon                     from '@mui/icons-material/Verified';
import LockOutlinedIcon                 from '@mui/icons-material/LockOutlined';
import AppsOutlinedIcon                 from '@mui/icons-material/AppsOutlined';
import { getUser, getMisAplicaciones, getCatalogoAplicaciones, isLogged, logout } from '../services/authApi';
import { getMiPerfil, getMiCarnet } from '../services/perfilApi';
import DialogCarnet from '../components/DialogCarnet';

// Presentación de cada app (ícono, color, etiqueta y texto de la card).
// Nombre, URL, estado (Activa) y orden vienen de la tabla Aplicaciones vía
// /api/ecolab/aplicaciones/catalogo — estado/url aquí son solo respaldo si
// el catálogo no responde.
const APLICACIONES = [
  { id:'b2b',     nombre:'B2B Marketplace', tag:'Mayoristas',
    descripcion:'Ofrece tus productos y compra a otros asociados de la red. Tienda comunitaria entre afiliados.',
    icono: StorefrontOutlinedIcon, color:'#1a4d2e', estado:'activa', url:'https://market.fenagro-oficial.org/' },
  { id:'pos',     nombre:'POS', tag:'Punto de venta',
    descripcion:'Caja registradora digital para tiendas, fruvers, droguerías, ferreterías y negocios de atención al público.',
    icono: PointOfSaleOutlinedIcon, color:'#2e7d32', estado:'activa', url:'https://pos.fenagro-oficial.org/' },
  { id:'aulas',   nombre:'Aulas Virtuales', tag:'Formación',
    descripcion:'Cursos para entender el sector solidario y fortalecer tus proyectos productivos.',
    icono: SchoolOutlinedIcon, color:'#1565c0', estado:'activa', url:'https://aulas.fenagro-oficial.org/' },
  { id:'news',    nombre:'News', tag:'Periódico',
    descripcion:'Noticias, anuncios, proyectos en marcha y beneficios. Ediciones por región y acceso a ediciones anteriores.',
    icono: NewspaperOutlinedIcon, color:'#6a1b9a', estado:'activa', url:'https://news.fenagro-oficial.org/' },
  // TODO: url temporal (localhost:3050) — reemplazar por la URL de producción de Red de Proyectos
  { id:'tracker', nombre:'Red de Proyectos', tag:'Banco de proyectos',
    descripcion:'Seguimiento del avance de los proyectos a los que te has inscrito tú y tu cooperativa.',
    icono: AccountTreeOutlinedIcon, color:'#ef6c00', estado:'activa', url:'https://localhost:3050' },
  { id:'coopnet', nombre:'CoopNet', tag:'Mi cooperativa',
    descripcion:'Estado de tu cuenta cooperativa, wallet, excedentes distribuidos y saldo disponible para comprar en B2B.',
    icono: AccountBalanceWalletOutlinedIcon, color:'#c8a84b', estado:'proximamente', url:null },
];

export default function MiPortal() {
  const user = getUser();

  const [perfil, setPerfil] = useState(null);
  const [carnet, setCarnet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open:false, msg:'' });
  const [dialogCarnetOpen, setDialogCarnetOpen] = useState(false);
  const [accesoBloqueado, setAccesoBloqueado] = useState(null); // null=verificando, false=ok, {msg,paso}=bloqueado
  const [appsAutorizadas, setAppsAutorizadas] = useState(null);
  const [catalogo, setCatalogo] = useState(null); // filas de la tabla Aplicaciones

  useEffect(() => {
    // Si no hay sesión válida, redirigir al login inmediatamente
    if (!isLogged()) { window.location.href = '/login'; return; }

    let cancelado = false;
    (async () => {
      try {
        const [p, c, apps, cat] = await Promise.allSettled([
          getMiPerfil(), getMiCarnet(), getMisAplicaciones(), getCatalogoAplicaciones(),
        ]);
        if (cancelado) return;
        if (cat.status === 'fulfilled' && Array.isArray(cat.value)) setCatalogo(cat.value);

        // Si getMiPerfil falla con 401, sesión inválida → login
        if (p.status === 'rejected') {
          const status = p.reason?.status;
          if (status === 401 || status === 403) {
            logout();
            window.location.href = '/login';
            return;
          }
        }

        if (p.status === 'fulfilled') {
          const perfilData = p.value;
          setPerfil(perfilData);

          // Verificar si el usuario tiene acceso completo al portal.
          // activo=false significa que el proceso de afiliación no está completo.
          if (perfilData.activo === false) {
            setAccesoBloqueado({
              msg: perfilData.pasoFaltante
                ? `Tu proceso de afiliación está incompleto. Paso pendiente: ${perfilData.pasoFaltante}`
                : 'Tu cuenta aún no ha sido activada por el administrador.',
              paso: perfilData.pasoFaltante,
            });
            setLoading(false);
            return;
          }
        }

        setAccesoBloqueado(false);
        if (c.status === 'fulfilled') setCarnet(c.value?.carnet || c.value);
        if (apps.status === 'fulfilled' && Array.isArray(apps.value)) {
          setAppsAutorizadas(apps.value.map(a => a.appCode));
        } else {
          setAppsAutorizadas([]);
        }
      } catch {}
      finally { if (!cancelado) setLoading(false); }
    })();
    return () => { cancelado = true; };
  }, []);

  // Cards = presentación local + datos de la tabla Aplicaciones (nombre, url, activa, orden).
  // Una app nueva en la tabla aparece con ícono genérico sin tocar código.
  const appsPortal = (() => {
    if (!catalogo) return APLICACIONES;
    const locales = Object.fromEntries(APLICACIONES.map(a => [a.id, a]));
    return catalogo.map(c => {
      const base = locales[c.appCode] || {
        id: c.appCode, tag: '', descripcion: c.descripcion || '',
        icono: AppsOutlinedIcon, color: '#1a4d2e',
      };
      return {
        ...base,
        id: c.appCode,
        nombre: c.nombre || base.nombre,
        url: c.urlBase || null,
        estado: c.activa && c.urlBase ? 'activa' : 'proximamente',
      };
    });
  })();

  const handleAppClick = (app) => {
    if (app.estado !== 'activa' || !app.url) {
      setToast({ open:true, msg:`${app.nombre} estará disponible muy pronto.` });
      return;
    }
    // Validar que el usuario tenga acceso autorizado a la app.
    const autorizada = appsAutorizadas?.includes(app.id);
    if (!autorizada) {
      setToast({ open:true, msg:`No tienes acceso a ${app.nombre}. Solicítalo al administrador.` });
      return;
    }
    window.open(app.url, '_blank', 'noopener,noreferrer');
  };

  const primerNombre = (perfil?.nombre || user?.nombre || '').split(' ')[0] || 'Asociado';
  const cooperativa = perfil?.cooperativaNombre || 'Red Alianza Solidaria';
  const carnetEstado = carnet?.estado || 'SIN_EMITIR';
  const carnetVigente = ['ACTIVO','EN_ORIENTACION'].includes(carnetEstado);

  // Spinner de carga inicial
  if (loading) return (
    <Box sx={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#f5f7f5' }}>
      <CircularProgress sx={{ color:'#1a4d2e' }}/>
    </Box>
  );

  // Acceso bloqueado — proceso de afiliación incompleto
  if (accesoBloqueado) return (
    <Box sx={{ minHeight:'100vh', background:'linear-gradient(135deg,#0a1f12 0%,#1a4d2e 60%,#2e7d32 100%)', display:'flex', alignItems:'center', justifyContent:'center', p:2 }}>
      <Box sx={{ background:'#fff', borderRadius:3, p:4, maxWidth:480, width:'100%', textAlign:'center', boxShadow:'0 8px 32px rgba(0,0,0,0.18)' }}>
        <Box sx={{ fontSize:56, mb:1 }}>🔒</Box>
        <Typography variant="h6" sx={{ fontWeight:700, color:'#1a4d2e', mb:1 }}>
          Acceso no disponible
        </Typography>
        <Typography variant="body2" sx={{ color:'#555', mb:2.5 }}>
          Tu proceso de afiliación aún no está completo. Debes terminarlo antes de poder acceder al portal.
        </Typography>
        {accesoBloqueado.msg && (
          <Box sx={{ background:'#fff8e1', border:'1px solid #ffe082', borderRadius:2, p:1.5, mb:2.5 }}>
            <Typography variant="body2" sx={{ color:'#e65100', fontWeight:600 }}>
              ⚠️ {accesoBloqueado.msg}
            </Typography>
          </Box>
        )}
        <Button variant="contained" fullWidth
          href="/afiliacion"
          sx={{ background:'linear-gradient(135deg,#1a4d2e,#43a047)', fontWeight:700, borderRadius:2, mb:1.5 }}>
          Continuar mi afiliación
        </Button>
        <Button variant="text" fullWidth
          onClick={() => { logout(); window.location.href = '/login'; }}
          sx={{ color:'#888', fontSize:'0.8rem' }}>
          Cerrar sesión
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight:'100vh', background:'#f5f7f5' }}>

      {/* Header con gradiente verde — pt grande para que la navbar transparente quede encima */}
      <Box sx={{
        background:'linear-gradient(135deg,#0a1f12 0%,#1a4d2e 50%,#2e7d32 100%)',
        color:'#fff',
        pt:{ xs:14, md:18 },   // espacio suficiente para que la navbar fixed no tape el contenido
        pb:{ xs:7, md:9 },
        px:2,
      }}>
        <Container maxWidth="lg">
          <Box sx={{ mb:3 }}>
            <Typography sx={{ fontSize:'0.8rem', opacity:0.75, letterSpacing:'0.1em', textTransform:'uppercase', mb:0.5 }}>
              Portal del afiliado
            </Typography>
            <Typography variant="h3" sx={{ fontWeight:700, lineHeight:1.2, fontSize:{ xs:'2rem', md:'2.5rem' } }}>
              Hola, {primerNombre}
            </Typography>
            <Typography sx={{ mt:0.5, opacity:0.85, fontSize:'1rem' }}>
              Bienvenido a tu espacio en la Red.
            </Typography>
          </Box>

          {/* Tarjetitas resumen */}
          <Grid container spacing={2}>
            {/* Cooperativa */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{
                background:'rgba(255,255,255,0.08)',
                backdropFilter:'blur(8px)',
                border:'1px solid rgba(255,255,255,0.18)',
                borderRadius:2, p:2.2,
                color:'#fff',
                height:'100%',
                display:'flex', alignItems:'center', gap:2,
              }}>
                <Avatar sx={{ background:'rgba(200,168,75,0.2)', color:'#f0d070', width:48, height:48 }}>
                  <BadgeOutlinedIcon/>
                </Avatar>
                <Box>
                  <Typography sx={{ fontSize:'0.72rem', opacity:0.7, letterSpacing:'0.08em', textTransform:'uppercase' }}>
                    Mi cooperativa
                  </Typography>
                  <Typography sx={{ fontWeight:700, fontSize:'1rem' }}>
                    {cooperativa}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Carnet */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                onClick={() => { if (carnet?.codigo) setDialogCarnetOpen(true); }}
                sx={{
                  background:'rgba(255,255,255,0.08)',
                  backdropFilter:'blur(8px)',
                  border:'1px solid rgba(255,255,255,0.18)',
                  borderRadius:2, p:2.2,
                  color:'#fff',
                  height:'100%',
                  display:'flex', alignItems:'center', gap:2,
                  cursor: carnet?.codigo ? 'pointer' : 'default',
                  transition:'all 0.2s ease',
                  '&:hover': carnet?.codigo ? {
                    background:'rgba(255,255,255,0.14)',
                    border:'1px solid rgba(255,255,255,0.3)',
                    transform:'translateY(-1px)',
                  } : {},
                }}
              >
                <Avatar sx={{
                  background: carnetVigente ? 'rgba(102,187,106,0.25)' : 'rgba(255,255,255,0.15)',
                  color: carnetVigente ? '#a5d6a7' : '#fff',
                  width:48, height:48,
                }}>
                  <VerifiedIcon/>
                </Avatar>
                <Box sx={{ flex:1 }}>
                  <Typography sx={{ fontSize:'0.72rem', opacity:0.7, letterSpacing:'0.08em', textTransform:'uppercase' }}>
                    Mi carnet
                  </Typography>
                  <Typography sx={{ fontWeight:700, fontSize:'1rem' }}>
                    {loading ? <CircularProgress size={14} sx={{ color:'#fff' }}/> : (carnet?.codigo || 'Sin emitir')}
                  </Typography>
                </Box>
                <Chip
                  label={loading ? '...' : carnetEstado.replace('_',' ')}
                  size="small"
                  sx={{
                    background: carnetVigente ? 'rgba(102,187,106,0.25)' : 'rgba(255,255,255,0.18)',
                    color:'#fff', fontWeight:700, letterSpacing:'0.04em', fontSize:'0.7rem',
                  }}
                />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Grilla de aplicaciones — flotando sobre el verde con mt negativo */}
      <Container maxWidth="lg" sx={{ mt:-4, pb:6, position:'relative', zIndex:2 }}>
        <Paper sx={{ borderRadius:3, p:{ xs:2.5, md:4 }, boxShadow:'0 8px 32px rgba(0,0,0,0.06)' }}>
          <Box sx={{ mb:3 }}>
            <Typography variant="h5" sx={{ fontWeight:700, color:'#1a3a1a' }}>
              Tus beneficios como asociado
            </Typography>
            <Typography sx={{ color:'#666', fontSize:'0.92rem', mt:0.5 }}>
              Plataformas y servicios disponibles para los miembros de la Red.
            </Typography>
          </Box>

          <Divider sx={{ mb:3 }}/>

          <Grid container spacing={2.5}>
            {appsPortal.map((app) => {
              const Icono = app.icono;
              const disponible = app.estado === 'activa';
              // Autorización: solo importa si la app está activa. Las 'proximamente'
              // siempre muestran el chip existente independientemente de acceso.
              const autorizada = appsAutorizadas?.includes(app.id);
              const bloqueada  = disponible && appsAutorizadas !== null && !autorizada;
              return (
                <Grid item xs={12} sm={6} md={4} key={app.id}>
                  <Card elevation={0} sx={{
                    height:'100%', borderRadius:2,
                    border:'1px solid #e0e7e0',
                    opacity: bloqueada ? 0.6 : 1,
                    transition:'all 0.2s ease',
                    '&:hover':{
                      borderColor: bloqueada ? '#e0e7e0' : app.color,
                      boxShadow: bloqueada ? 'none' : `0 8px 24px ${app.color}1a`,
                      transform: bloqueada ? 'none' : 'translateY(-2px)',
                    },
                  }}>
                    <CardActionArea onClick={() => handleAppClick(app)} sx={{ height:'100%', p:0.5 }}>
                      <CardContent sx={{ p:2.5 }}>
                        <Box sx={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', mb:1.5 }}>
                          <Avatar sx={{
                            background:`${app.color}15`, color:app.color,
                            width:52, height:52, borderRadius:2,
                          }}>
                            <Icono sx={{ fontSize:28 }}/>
                          </Avatar>
                          {!disponible && (
                            <Chip label="Próximamente" size="small" sx={{
                              background:'#fff8e1', color:'#bf6a00',
                              fontWeight:700, fontSize:'0.66rem', letterSpacing:'0.04em',
                            }}/>
                          )}
                          {bloqueada && (
                            <Chip icon={<LockOutlinedIcon sx={{ fontSize:14 }}/>} label="Sin acceso" size="small" sx={{
                              background:'#fdecea', color:'#c62828',
                              fontWeight:700, fontSize:'0.66rem', letterSpacing:'0.04em',
                              '& .MuiChip-icon': { color:'#c62828', ml:0.5 },
                            }}/>
                          )}
                        </Box>
                        <Typography sx={{ fontWeight:700, fontSize:'1.1rem', color:'#1a1a1a', mb:0.3 }}>
                          {app.nombre}
                        </Typography>
                        <Typography sx={{ fontSize:'0.72rem', color:app.color, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', mb:1 }}>
                          {app.tag}
                        </Typography>
                        <Typography sx={{ fontSize:'0.85rem', color:'#666', lineHeight:1.55 }}>
                          {app.descripcion}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Paper>

        <Typography sx={{ textAlign:'center', color:'#999', fontSize:'0.78rem', mt:4 }}>
          FENAGRO APL - Alianza Solidaria - {new Date().getFullYear()} · Powered by Redeprog
        </Typography>
      </Container>

      <DialogCarnet
        open={dialogCarnetOpen}
        onClose={() => setDialogCarnetOpen(false)}
        carnet={carnet}
        fotoUrl={perfil?.fotoUrl}
      />

      <Snackbar
        open={toast.open} autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open:false })}
        anchorOrigin={{ vertical:'bottom', horizontal:'center' }}>
        <Alert severity="info" variant="filled" onClose={() => setToast({ ...toast, open:false })}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
